import Styles from '../../AutopilotPlansModal.module.scss'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Modal } from 'almanac-ui/Modal'
import { CurrentPlan, PlanDetails, nameMap } from './components/PlanDetails/PlanDetails'
import { Text } from 'blaze-ui/Text/Text'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { Button } from 'blaze-ui/Button'
import { useRootStore } from 'stores/RootStore'
import { AutopilotPlan } from 'components/Settings/Workspace/VirtualMarketerUpgradeCta/types'
import { formatPrice, calculateMonthlyExtraAccountCharges } from 'components/Settings/TieredBilling/utils'
import dayjs from 'dayjs'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

export const findMonthlyPrice = (plan: AutopilotPlan, intervalCount = 1, currentSubscription?: any) => {
  const monthlyPrice = plan.prices.find((p) => p.recurring.interval_count === intervalCount)
  let totalPrice = monthlyPrice ? Math.ceil(monthlyPrice.unit_amount / intervalCount / 100) : 0

  // Add monthly extra account charges
  const extraAccountCharges = calculateMonthlyExtraAccountCharges(currentSubscription)
  totalPrice += Math.ceil(extraAccountCharges)

  return totalPrice
}

export const findCreditLimit = (plan: AutopilotPlan): number | undefined => {
  const limit = plan.prices[0]?.metadata?.credit_limit
  return limit != null ? Number(limit) : undefined
}

export const Plans = ({
  onBack,
  onCancelSubscription,
  onSelectPlan,
  isProcessing,
}: {
  onBack: () => void
  onCancelSubscription: () => void
  onSelectPlan: (plan: AutopilotPlan | null) => void
  isProcessing: boolean
}) => {
  const { isMobile } = useResponsiveDesign()
  const { tierUsageStore } = useRootStore()
  const { autopilotPlans, currentSubscription } = tierUsageStore
  const workspace = useCurrentWorkspace()

  if (!currentSubscription) {
    return null
  }

  const price = currentSubscription.prices[0].unit_amount
  const intervalCount = currentSubscription.prices[0].recurring.interval_count

  // Calculate total price including extra accounts
  const basePriceMonthly = price / intervalCount / 100
  const extraAccountCharges = calculateMonthlyExtraAccountCharges(currentSubscription)
  const totalMonthlyPrice = basePriceMonthly + extraAccountCharges

  let currentPlan: CurrentPlan = !workspace?.is_ap_freemium
    ? {
        key: currentSubscription?.plan,
        length: currentSubscription?.prices[0].recurring.interval_count,
        monthlyPrice: formatPrice(totalMonthlyPrice),
      }
    : {
        key: PaymentPlanKey.Free,
        length: 1,
        monthlyPrice: 0,
      }
  const starterPlan = autopilotPlans.find((plan) => plan.key === PaymentPlanKey.BlazeAutopilotStarter)
  const isOnPioneerPlan = currentPlan.key === PaymentPlanKey.VirtualMarketer
  const growthPlan = autopilotPlans.find((plan) => plan.key === PaymentPlanKey.BlazeAutopilotGrowth)
  const enterprisePlan = autopilotPlans.find((plan) => plan.key === PaymentPlanKey.BlazeAutopilotEnterprise)
  const isTrialing = currentSubscription?.status === 'trialing'
  const trialEnded = currentSubscription?.trial_ended
  const showCancelSubscriptionButton = !workspace?.is_ap_freemium && !isTrialing && !currentSubscription.cancel_at
  const currentSubscriptionCreditLimit = currentSubscription.prices[0].metadata.credit_limit
  const showCurrentPrice = isTrialing || trialEnded

  if (showCurrentPrice && starterPlan && growthPlan && enterprisePlan) {
    switch (currentSubscription.plan) {
      case PaymentPlanKey.BlazeAutopilotStarter:
        currentPlan.monthlyPrice = formatPrice(findMonthlyPrice(starterPlan, 1, currentSubscription))
        break
      case PaymentPlanKey.BlazeAutopilotGrowth:
        currentPlan.monthlyPrice = formatPrice(findMonthlyPrice(growthPlan, 1, currentSubscription))
        break
      case PaymentPlanKey.BlazeAutopilotEnterprise:
        currentPlan.monthlyPrice = formatPrice(findMonthlyPrice(enterprisePlan, 1, currentSubscription))
        break
      default:
        break
    }
  }

  const planName = nameMap[currentSubscription.plan]
  const isCanceled = currentSubscription.status === 'canceled'

  const getHeaderText = (): React.ReactNode => {
    if (isTrialing && currentSubscription.trial_days_left != null && currentSubscription.trial_days_left > 0) {
      return (
        <Text color="var(--dark-60)" variant={isMobile ? 'metadata' : undefined}>
          Your {planName} trial ends in{' '}
          {currentSubscription.trial_days_left === 1 ? '1 day' : `${currentSubscription.trial_days_left} days`}
        </Text>
      )
    }
    if (currentSubscription.trial_ended) {
      return (
        <Text color="var(--dark-60)" variant={isMobile ? 'metadata' : undefined}>
          Your {planName} trial has ended
        </Text>
      )
    }
    if (currentSubscription.cancel_at && !isCanceled) {
      return (
        <Text color="var(--dark-60)" variant={isMobile ? 'metadata' : undefined}>
          Your {planName} plan ends {dayjs(currentSubscription.cancel_at).format('MMM D')} at{' '}
          {dayjs(currentSubscription.cancel_at).format('ha')} PT
        </Text>
      )
    }
    if (isCanceled) {
      return (
        <Text color="var(--dark-60)" variant={isMobile ? 'metadata' : undefined}>
          Your {planName} plan is canceled. Select plan to activate.
        </Text>
      )
    }
    return null
  }

  return (
    <>
      <Modal.Header
        actions={
          <>
            {!isMobile && showCancelSubscriptionButton && (
              <Button variant="tertiary" onPress={onCancelSubscription}>
                Cancel Subscription
              </Button>
            )}
          </>
        }
      >
        <div className={Styles.header}>
          <div className={Styles.left}>
            <div className={Styles.backAndTitle}>
              <div className={Styles.titleAndTrial}>
                <Heading level={isMobile ? 5 : 3}>Choose your Blaze plan</Heading>
                {getHeaderText()}
              </div>
            </div>
          </div>
        </div>
      </Modal.Header>
      <Modal.Content className={Styles.content}>
        {isOnPioneerPlan && currentSubscription?.status !== 'canceled' && (
          <div className={Styles.pioneerCta}>
            <div className={Styles.header}>
              <Heading level={5}>You're on the Pioneer plan</Heading>
            </div>
            <Text>
              You have unlimited channels, posts, and seats. You can purchase credits to generate premium content like
              Stylized Images and AI Videos. Since the Pioneer plan is for early Autopilot adopters, it is not available
              again once canceled or changed.
            </Text>
          </div>
        )}
        <div className={Styles.plansContent}>
          <div className={Styles.individualPlans}>
            <div className={Styles.plans}>
              {starterPlan && currentSubscription?.plan && (
                <PlanDetails
                  planKey={starterPlan.key}
                  monthlyPrice={findMonthlyPrice(starterPlan, 1, currentSubscription)}
                  creditLimit={
                    !isTrialing && currentSubscription.plan === starterPlan.key && currentSubscriptionCreditLimit
                      ? Number(currentSubscriptionCreditLimit)
                      : findCreditLimit(starterPlan)
                  }
                  currentPlan={currentPlan}
                  onSelectPlan={() => onSelectPlan(starterPlan)}
                  isTrialing={isTrialing}
                  isProcessing={isProcessing}
                />
              )}
            </div>
          </div>
          <div className={Styles.teamPlans}>
            <div className={Styles.plans}>
              {growthPlan && currentSubscription?.plan && (
                <PlanDetails
                  planKey={growthPlan.key}
                  monthlyPrice={findMonthlyPrice(growthPlan, 1, currentSubscription)}
                  creditLimit={
                    !isTrialing && currentSubscription.plan === growthPlan.key && currentSubscriptionCreditLimit
                      ? Number(currentSubscriptionCreditLimit)
                      : findCreditLimit(growthPlan)
                  }
                  currentPlan={currentPlan}
                  onSelectPlan={() => onSelectPlan(growthPlan)}
                  isTrialing={currentSubscription?.status === 'trialing'}
                  isProcessing={isProcessing}
                />
              )}
              {enterprisePlan && currentSubscription?.plan && (
                <PlanDetails
                  planKey={enterprisePlan.key}
                  monthlyPrice={findMonthlyPrice(enterprisePlan, 1, currentSubscription)}
                  creditLimit={findCreditLimit(enterprisePlan)}
                  currentPlan={currentPlan}
                  onSelectPlan={() => onSelectPlan(enterprisePlan)}
                  isTrialing={currentSubscription?.status === 'trialing'}
                  isProcessing={isProcessing}
                />
              )}
            </div>
          </div>
        </div>
        {isMobile && showCancelSubscriptionButton && (
          <div className={Styles.cancelSubscriptionBottom}>
            <Button variant="tertiary" onPress={onCancelSubscription} className={Styles.cancelSubscriptionBottomButton}>
              Cancel Subscription
            </Button>
          </div>
        )}
      </Modal.Content>
      {!isMobile && (
        <Modal.Footer>
          <Modal.FooterContent slot="left">
            <Button variant="tertiary" onPress={onBack} data-testid="plans-back-button">
              Back
            </Button>
          </Modal.FooterContent>
        </Modal.Footer>
      )}
    </>
  )
}
